import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  CpuChipIcon,
  SparklesIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { documentsApi, type GenerateDocumentParams } from '../../api/documents';
import { useFileStore } from '../../stores/fileStore';
import { useToast } from '../ui/Toaster';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import type { DocumentType } from '@affogato/shared';

interface CreateDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string | null;
}

const documentTypes: Array<{
  type: DocumentType;
  icon: typeof DocumentTextIcon;
  labelKey: string;
  descKey: string;
}> = [
  {
    type: 'proposal',
    icon: DocumentTextIcon,
    labelKey: 'documents:types.proposal',
    descKey: 'documents:types.proposalDesc',
  },
  {
    type: 'solution-architecture',
    icon: CpuChipIcon,
    labelKey: 'documents:types.solutionArchitecture',
    descKey: 'documents:types.solutionArchitectureDesc',
  },
  {
    type: 'sla',
    icon: ClipboardDocumentCheckIcon,
    labelKey: 'documents:types.sla',
    descKey: 'documents:types.slaDesc',
  },
];

export function CreateDocumentModal({ isOpen, onClose, folderId }: CreateDocumentModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const addFile = useFileStore((s) => s.addFile);

  const [selectedType, setSelectedType] = useState<DocumentType>('proposal');
  const [instructions, setInstructions] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!instructions.trim()) {
      toast.error(t('documents:errors.instructionsRequired'));
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    try {
      const params: GenerateDocumentParams = {
        type: selectedType,
        instructions: instructions.trim(),
        folderId,
        locale: i18n.language,
      };

      const response = await documentsApi.generate(params);

      // Add file to store
      addFile(response.file);

      toast.success(t('documents:generateSuccess'));
      onClose();

      // Navigate to editor
      navigate(`/documents/${response.file.id}`);
    } catch (error: any) {
      console.error('Document generation error:', error);
      if (error.message?.includes('AI_NOT_CONFIGURED')) {
        setGenerationError(t('documents:errors.aiNotConfigured'));
      } else if (error.message?.includes('RATE_LIMITED')) {
        setGenerationError(t('documents:errors.rateLimited'));
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        setGenerationError(t('documents:errors.networkError') || 'Error de conexión. Verifica tu internet e intenta de nuevo.');
      } else {
        setGenerationError(error.message || t('documents:errors.generateFailed'));
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      setSelectedType('proposal');
      setInstructions('');
      setGenerationError(null);
      onClose();
    }
  };

  const getGeneratingMessage = () => {
    switch (selectedType) {
      case 'proposal':
        return t('documents:generating.proposal') || 'Generando propuesta comercial...';
      case 'solution-architecture':
        return t('documents:generating.architecture') || 'Generando arquitectura de solución...';
      case 'sla':
        return t('documents:generating.sla') || 'Generando acuerdo de nivel de servicio...';
      default:
        return t('documents:generating.default') || 'Generando documento...';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-primary-500" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('documents:createTitle')}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isGenerating}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-400 disabled:opacity-50"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Document Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    {t('documents:typeLabel')}
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {documentTypes.map((docType) => {
                      const Icon = docType.icon;
                      const isSelected = selectedType === docType.type;
                      return (
                        <button
                          key={docType.type}
                          type="button"
                          onClick={() => setSelectedType(docType.type)}
                          disabled={isGenerating}
                          className={`p-3 rounded-lg border-2 text-left transition-all overflow-hidden ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                          } disabled:opacity-50`}
                        >
                          <Icon
                            className={`h-5 w-5 mb-2 flex-shrink-0 ${
                              isSelected
                                ? 'text-primary-500'
                                : 'text-gray-400 dark:text-slate-500'
                            }`}
                          />
                          <div
                            className={`font-medium text-sm truncate ${
                              isSelected
                                ? 'text-primary-700 dark:text-primary-400'
                                : 'text-gray-900 dark:text-white'
                            }`}
                          >
                            {t(docType.labelKey)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1 line-clamp-2">
                            {t(docType.descKey)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Instructions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    {t('documents:instructionsLabel')}
                  </label>
                  <textarea
                    value={instructions}
                    onChange={(e) => {
                      setInstructions(e.target.value);
                      if (generationError) setGenerationError(null);
                    }}
                    placeholder={t(`documents:instructionsPlaceholder.${selectedType}`)}
                    readOnly={isGenerating}
                    rows={5}
                    className={`w-full px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none ${
                      isGenerating ? 'cursor-not-allowed' : ''
                    }`}
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {t('documents:instructionsHelp')}
                    </p>
                    <span className={`text-xs ${instructions.trim().length < 10 ? 'text-amber-500' : 'text-gray-400 dark:text-slate-500'}`}>
                      {instructions.trim().length} {t('documents:chars') || 'caracteres'}
                    </span>
                  </div>
                </div>

                {/* Error Display */}
                {generationError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{generationError}</p>
                  </div>
                )}

                {/* Generation Status */}
                {isGenerating && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Spinner size="sm" />
                      <div>
                        <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                          {getGeneratingMessage()}
                        </p>
                        <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                          {t('documents:generating.hint') || 'Esto puede tomar unos segundos'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-slate-700">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isGenerating}
                >
                  {t('common:buttons.cancel')}
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating || !instructions.trim()}
                >
                  {isGenerating ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t('documents:generating.button') || 'Generando...'}
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-4 w-4 mr-2" />
                      {t('documents:generateButton')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
